import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Clock,
  MessageSquare,
  Briefcase,
  Loader2,
  Filter,
} from "lucide-react";

const UPDATE_TYPE_CONFIG = {
  progress: { label: "Progress", icon: TrendingUp, color: "bg-blue-100 text-blue-700" },
  milestone: { label: "Milestone", icon: CheckCircle, color: "bg-green-100 text-green-700" },
  blocker: { label: "Blocker", icon: AlertCircle, color: "bg-amber-100 text-amber-700" },
  note: { label: "Note", icon: MessageSquare, color: "bg-gray-100 text-gray-700" },
  general: { label: "General", icon: Clock, color: "bg-purple-100 text-purple-700" },
};

const ClientUpdates = () => {
  const { token, API } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  useEffect(() => {
    fetchProjects();
    fetchUpdates();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/client/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchUpdates = async () => {
    try {
      const response = await fetch(`${API}/client/updates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUpdates(data.updates || []);
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Filter updates
  const filteredUpdates = updates.filter((update) => {
    const matchesProject = selectedProject === "all" || update.project_id === selectedProject;
    const matchesType = selectedType === "all" || update.update_type === selectedType;
    return matchesProject && matchesType;
  });

  // Group updates by date
  const groupedUpdates = filteredUpdates.reduce((groups, update) => {
    const date = new Date(update.created_at).toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(update);
    return groups;
  }, {});

  if (loading) {
    return (
      <ClientLayout activeNav="updates">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-rnb-blue" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout activeNav="updates">
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">Project Updates</h1>
            <p className="text-sm text-gray-400 mt-0.5">Stay informed about the latest progress on your projects.</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Filter by:</span>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger data-testid="project-filter">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-[180px]">
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(UPDATE_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Updates List */}
        {filteredUpdates.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No Updates Yet</h3>
              <p className="text-gray-500">
                Project updates from your consultants will appear here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedUpdates).map(([date, dateUpdates]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-sm font-medium text-gray-500 px-2">
                    {date}
                  </span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>

                {/* Updates for this date */}
                <div className="space-y-4">
                  {dateUpdates.map((update) => {
                    const typeConfig = UPDATE_TYPE_CONFIG[update.update_type] || UPDATE_TYPE_CONFIG.general;
                    const TypeIcon = typeConfig.icon;

                    return (
                      <Card 
                        key={update.update_id} 
                        className="hover:shadow-md transition-shadow"
                        data-testid={`update-card-${update.update_id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                              <TypeIcon className="h-5 w-5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h3 className="font-semibold text-rnb-navy">
                                  {update.title}
                                </h3>
                                <Badge variant="outline" className="shrink-0">
                                  {typeConfig.label}
                                </Badge>
                              </div>

                              {update.content && (
                                <p className="text-gray-600 mb-3 whitespace-pre-wrap">
                                  {update.content}
                                </p>
                              )}

                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                {/* Project Badge */}
                                <div className="flex items-center gap-1">
                                  <Briefcase className="h-3.5 w-3.5" />
                                  <span>{update.project_name || "Unknown Project"}</span>
                                </div>

                                {/* Author */}
                                <span>by {update.created_by_name}</span>

                                {/* Time */}
                                <span>
                                  {new Date(update.created_at).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {filteredUpdates.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500">
            Showing {filteredUpdates.length} update{filteredUpdates.length !== 1 ? "s" : ""}
            {selectedProject !== "all" || selectedType !== "all" ? " (filtered)" : ""}
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientUpdates;
